import React, { useState } from "react";
import { Loader2, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLang, STR } from "@/i18n/translations";
import api from "@/lib/api";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t(STR.contact.success));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(t({ fr: "Erreur, réessayez.", en: "Error, try again." }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <p className="eyebrow mb-5">{t(STR.contact.eyebrow)}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">{t(STR.contact.title)}</h1>
        <div className="gold-line w-24 my-10" />

        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12">
          <div className="space-y-6">
            {[
              { Icon: Mail, label: "Email", value: "silyagency@gmail.com", href: "mailto:silyagency@gmail.com" },
              { Icon: Phone, label: t(STR.booking.fields.phone), value: "+33 (0)7 58 49 04 59", href: "tel:+33758490459" },
              { Icon: MapPin, label: t({ fr: "Adresse", en: "Address" }), value: "1 rue Henri Gonnard, 42000 Saint-Étienne", href: "https://maps.google.com/?q=1+rue+Henri+Gonnard+42000+Saint-Etienne" },
            ].map(({ Icon, label, value, href }, i) => (
              <a key={i} href={href} className="card-dark rounded-xl p-6 flex items-center gap-4 block" data-testid={`contact-info-${i}`}>
                <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-cream/50">{label}</p>
                  <p className="text-cream">{value}</p>
                </div>
              </a>
            ))}
            <a href="https://wa.me/33758490459" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn"
               className="rounded-xl p-6 flex items-center gap-4 bg-[#25D366]/10 border border-[#25D366]/30 hover:border-[#25D366] transition-colors">
              <div className="w-11 h-11 rounded-lg bg-[#25D366]/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
              </div>
              <p className="text-cream font-medium">{t(STR.contact.whatsapp)}</p>
            </a>
          </div>

          <form onSubmit={submit} className="card-dark rounded-2xl p-8 lg:p-10 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-widest text-cream/50 mb-2 block">{t(STR.booking.fields.name)} <span className="text-gold">*</span></label>
                <input required value={form.name} onChange={set("name")} data-testid="contact-name" className="w-full bg-white/5 border border-gold/20 rounded px-3 py-3 text-sm outline-none focus:border-gold" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-cream/50 mb-2 block">Email <span className="text-gold">*</span></label>
                <input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" className="w-full bg-white/5 border border-gold/20 rounded px-3 py-3 text-sm outline-none focus:border-gold" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-cream/50 mb-2 block">{t(STR.contact.subject)}</label>
              <input value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="w-full bg-white/5 border border-gold/20 rounded px-3 py-3 text-sm outline-none focus:border-gold" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-cream/50 mb-2 block">{t(STR.booking.fields.message)} <span className="text-gold">*</span></label>
              <textarea rows={6} required value={form.message} onChange={set("message")} data-testid="contact-message" className="w-full bg-white/5 border border-gold/20 rounded px-3 py-3 text-sm outline-none focus:border-gold resize-none" />
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="btn-gold rounded-full w-full py-4 flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t(STR.common.send)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
